import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Package, Search, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { getImageUrl } from "../../utils/imageUtils";
import { useInfinityCustomer } from "../../context/InfinityCustomerContext";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

/**
 * Product catalogue (TR-9 3.1/3.2) — edisplayroom-style two-column layout: a left
 * collection sidebar + product grid. The channel comes from the route/param:
 * "Jewelry" = infinity (the default — families only see IT products unless they ask
 * for more), "All Products" = ?channel=all (IT + edisplay, and the server returns
 * nothing unless the funeral home sells edisplay). Cards link to the product details
 * page (add-to-cart happens there).
 */
const CHANNELS = ["infinity", "edisplay", "all"];
export default function InfinityShop() {
  const { http, isAuthed, theme, basePath } = useInfinityCustomer();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const channel = CHANNELS.includes(params.get("channel")) ? params.get("channel") : "infinity";

  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [activeCol, setActiveCol] = useState(params.get("collection_id") || "");
  const [keyword, setKeyword] = useState("");
  const [debKeyword, setDebKeyword] = useState("");
  const [colSearch, setColSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthed) { navigate(`${basePath}/login`); return; }
    setActiveCol(""); // reset the collection filter when switching channels
    http.get(`/collections?channel=${channel}`).then((r) => setCollections(r.data || [])).catch(() => setCollections([]));
  }, [channel]); // eslint-disable-line

  useEffect(() => { const t = setTimeout(() => setDebKeyword(keyword.trim()), 300); return () => clearTimeout(t); }, [keyword]);

  useEffect(() => {
    if (!isAuthed) return;
    setLoading(true);
    const p = new URLSearchParams({ channel, limit: "60" });
    if (debKeyword) p.set("keyword", debKeyword);
    if (activeCol) p.set("collection_id", activeCol);
    http.get(`/catalog?${p.toString()}`)
      .then((r) => setProducts(r.data?.products || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [channel, debKeyword, activeCol]); // eslint-disable-line

  const filteredCols = collections.filter((c) => c.name.toLowerCase().includes(colSearch.toLowerCase()));
  const primary = theme.primary_color || "#2E72BC";
  const accent = theme.accent_color || "#E0A93C";

  return (
    <div className="flex flex-col md:flex-row gap-8">
      {/* Left collection sidebar — the list scrolls independently so long
          collection lists never get cut off (TR-10 3.1). */}
      <aside className="md:w-64 shrink-0">
        <div className="md:sticky md:top-24 rounded-xl border overflow-hidden flex flex-col md:max-h-[calc(100vh-8rem)]">
          <div className="px-4 py-3 text-white font-semibold text-sm shrink-0" style={{ backgroundColor: primary }}>Collections</div>
          <div className="p-2 shrink-0 border-b">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input className="pl-8 h-9" placeholder="Search collections" value={colSearch} onChange={(e) => setColSearch(e.target.value)} />
            </div>
          </div>
          <div className="p-2 overflow-y-auto flex-1">
            <button onClick={() => setActiveCol("")}
              className="w-full text-left px-3 py-2 text-sm rounded-md border-l-4"
              style={{ borderLeftColor: !activeCol ? accent : "transparent", background: !activeCol ? "#F1F5F9" : "transparent" }}>
              All collections
            </button>
            {filteredCols.map((c) => (
              <button key={c.id} onClick={() => setActiveCol(c.id)}
                className="w-full text-left px-3 py-2 text-sm rounded-md border-l-4 truncate"
                style={{ borderLeftColor: activeCol === c.id ? accent : "transparent", background: activeCol === c.id ? "#F1F5F9" : "transparent" }}>
                {c.name}
              </button>
            ))}
            {filteredCols.length === 0 ? <p className="px-3 py-2 text-xs text-slate-400">No collections</p> : null}
          </div>
        </div>
      </aside>

      {/* Product grid */}
      <div className="flex-1">
        {/* Sticky heading + full-width search below the 4rem header (TR-10 3.3). */}
        <div className="sticky top-16 z-10 -mx-4 px-4 py-3 mb-4 space-y-3"
          style={{ background: theme.background_color || "#F8FAFC" }}>
          <h2 className="text-xl font-bold text-slate-900">{channel === "all" ? "All Products" : channel === "edisplay" ? "Urn store" : "Fingerprint Jewelry"}</h2>
          <div className="relative w-full">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9 h-11" placeholder="Search products" value={keyword} onChange={(e) => setKeyword(e.target.value)} />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-10 h-10 animate-spin" style={{ color: primary }} /></div>
        ) : products.length === 0 ? (
          <p className="text-center text-slate-400 py-12">No products found.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p) => (
              <button key={p.product_id} onClick={() => navigate(`${basePath}/product/${p.product_id}?channel=${channel}`)}
                className="text-left bg-white rounded-2xl border overflow-hidden hover:shadow-lg transition-shadow">
                <div className="aspect-[4/5] bg-slate-50 flex items-center justify-center overflow-hidden">
                  {p.image_url ? <img src={getImageUrl(p.image_url)} alt={p.name} className="w-full h-full object-cover" /> : <Package className="w-10 h-10 text-slate-300" />}
                </div>
                <div className="p-3">
                  <p className="text-sm font-medium text-slate-900 line-clamp-2 min-h-[2.5rem]">{p.name}</p>
                  <p className="text-sm font-semibold mt-1" style={{ color: accent }}>{money(p.display_price ?? p.base_price)}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
